import * as React from 'react';
import { SliderHandleIcon } from '../../icons/Icons';
import { DefaultFocusIndicator } from '../../styles/FocusIndicators';
import { TabStyles } from '../../styles/TabStyles';

const sliderHandleIconHalfSize = { w: Math.ceil(SliderHandleIcon.width / 2), h: Math.ceil(SliderHandleIcon.height / 2) };

const sliderHeight = 40;
// It's just a coincidence that the following two constants are equal
const sliderTickHeight = 4;
const sliderTrackHeight = 4;

const sliderHandleSize = { w: 40, h: 40 };
const sliderHandleHalfSize = { w: sliderHandleSize.w / 2, h: sliderHandleSize.h / 2 };
const sliderHandlePressIndicationBarHeight = 16;

const sliderSpaceBetweenTickBarAndTrack = 11;

const sliderLeftRightPadding = 14;

export const getSliderTrackStyles = () => {
  const basicStyles = {
    Track: <React.CSSProperties> {
      position: 'absolute',

      left: sliderHandleIconHalfSize.w,
      right: sliderHandleIconHalfSize.w,

      backgroundColor: TabStyles.Colors.L3,
      height: sliderTrackHeight,
      top: (sliderHeight -  sliderTrackHeight) / 2,
    },
    RangeIndicatorBar: <React.CSSProperties> {
      position: 'absolute',
      top: 0,
      bottom: 0,
      backgroundColor: TabStyles.Colors.L7,
    },
    DataBar: <React.CSSProperties> {
      position: 'absolute',
      top: 0,
      bottom: 0,
      backgroundColor: TabStyles.Colors.L8,
    }
  };
  return {
    ...basicStyles,
    RangeIndicatorBarDarkened: <React.CSSProperties> {
      ...basicStyles.RangeIndicatorBar,
      backgroundColor: TabStyles.Colors.L8,
    },
    RangeIndicatorBarFocused: <React.CSSProperties> {
      ...basicStyles.RangeIndicatorBar,
      backgroundColor: TabStyles.Colors.Focus,
      boxShadow: DefaultFocusIndicator.boxShadow
    },
    DataBarDarkened: <React.CSSProperties> {
      ...basicStyles.DataBar,
      backgroundColor: TabStyles.Colors.L9,
    },
    DataBarFocused: <React.CSSProperties> {
      ...basicStyles.DataBar,
      backgroundColor: `rgb(24, 71, 102)`,
      boxShadow: DefaultFocusIndicator.boxShadow
    }
  };
};

export const getSliderTickBarStyles = () => {
  return {
    Bar: <React.CSSProperties> {
      position: 'absolute',

      left: sliderHandleIconHalfSize.w,
      // The + 1 here is to make the spread of the ticks match the width of the track bar (above).  Since we position
      // the ticks using `left: X%`, the far right tick is actually 1px into the `right` space.  The + 1
      // counterintuitivly shrinks the width of the tick bar so that the final tick is now directly above the right end
      // of the slider track.
      right: sliderHandleIconHalfSize.w + 1,

      height: sliderTickHeight,
      top: (sliderHeight - sliderTrackHeight) / 2 - sliderSpaceBetweenTickBarAndTrack - sliderTickHeight,
    },
    Tick: <React.CSSProperties> {
      position: 'absolute',
      borderLeft: `solid 1px ${TabStyles.Colors.L6}`,
      height: sliderTickHeight,
      width: 0,
    }
  };
};

export const getSliderHandleStyles = () => {
  const basicStyles = {
    PositioningContainer: <React.CSSProperties> {
      position: 'absolute',
    },
    PressIndicationBar: <React.CSSProperties> {
      position: 'absolute',
      borderLeft: `solid 2px ${TabStyles.Colors.L9}`,
      // If we dont push it to the left by 1 px the looks off center from the circle icon
      marginLeft: -1,
      width: 0,
      height: sliderHandlePressIndicationBarHeight,
      top: -3,
    },
    HandleInteractiveArea: <React.CSSProperties> {
      position: 'absolute',
      touchAction: 'none',
      width: SliderHandleIcon.width,
      height: SliderHandleIcon.height,

      boxSizing: 'content-box',

      paddingTop: sliderHandleHalfSize.h - sliderHandleIconHalfSize.h,
      paddingBottom: sliderHandleHalfSize.h - sliderHandleIconHalfSize.h,
      paddingLeft: sliderHandleHalfSize.w - sliderHandleIconHalfSize.w,
      paddingRight: sliderHandleHalfSize.w - sliderHandleIconHalfSize.w,
      marginLeft: -1 * sliderHandleHalfSize.w,

      WebkitTouchCallout: 'none', /* iOS Safari */
      WebkitUserSelect: 'none', /* Safari */
      MozUserSelect: 'none', /* Firefox */
      msUserSelect: 'none', /* Internet Explorer/Edge */
      userSelect: 'none',
    },
    HandleIcon: <React.CSSProperties> {
      position: 'absolute',
      touchAction: 'none',
      cursor: 'pointer',
      width: SliderHandleIcon.width,
      height: SliderHandleIcon.height,
      marginLeft: -1 * (sliderHandleIconHalfSize.w),
      marginTop: (sliderHandleHalfSize.h - sliderHandleIconHalfSize.h),

      outline: 'none',

      WebkitTouchCallout: 'none', /* iOS Safari */
      WebkitUserSelect: 'none', /* Safari */
      MozUserSelect: 'none', /* Firefox */
      msUserSelect: 'none', /* Internet Explorer/Edge */
      userSelect: 'none',

      WebkitTapHighlightColor: 'transparent',
    }
  };
  return {
    ...basicStyles,
    HandleInteractiveAreaLeft: <React.CSSProperties> {
      ...basicStyles.HandleInteractiveArea,
      paddingLeft: basicStyles.HandleInteractiveArea.paddingLeft as number - 5,
      paddingRight: basicStyles.HandleInteractiveArea.paddingRight as number + 5,
    },
    HandleInteractiveAreaRight: <React.CSSProperties> {
      ...basicStyles.HandleInteractiveArea,
      paddingLeft: basicStyles.HandleInteractiveArea.paddingLeft as number + 6,
      paddingRight: basicStyles.HandleInteractiveArea.paddingRight as number - 4,
    },
    HandleIconLeft: <React.CSSProperties> {
      ...basicStyles.HandleIcon,
      marginLeft: basicStyles.HandleIcon.marginLeft as number - 5,
    },
    HandleIconRight: <React.CSSProperties> {
      ...basicStyles.HandleIcon,
      marginLeft: basicStyles.HandleIcon.marginLeft as number + 6,
    },
    DisabledHandleMixin: <React.CSSProperties> {
      touchAction: undefined,
    },
    DisabledHandleInnerFocusAreaMixin: <React.CSSProperties> {
      touchAction: undefined,
      cursor: undefined,
    }
  };
};

export const getSliderHandleContainerStyles = () => {
  return {
    Container: <React.CSSProperties> {
      position: 'absolute',
      top: 0, bottom: 0, left: 0, right: 0,
      // We restrict the horizontal space (that the handle can be positioned in) a bit and take away half the width of
      // the handle icon on the left and right sides.  This way, because we center the handle at a given position, if
      // the handle is at the far left it isn't half outside the bounds of the slider.  Same thing if it is at the far
      // right.
      //
      // Also the + 1 here is to:
      // A. Make the handle area occupy the same amount of horizontal space as the tick bar (which is one pixel smaller
      // than the track, see above)
      // B. accommodate the press indicator bar being moved to the left 1px (so it will line up w/ the ticks)
      marginLeft: sliderHandleIconHalfSize.w + 1,
      marginRight: sliderHandleIconHalfSize.w,
    },
    RangeHandleArea: <React.CSSProperties> {
      position: 'absolute',
      cursor: 'pointer',
      height: SliderHandleIcon.height,
      outline: 'none',
      top: sliderHandleHalfSize.h - sliderHandleIconHalfSize.h,
    }
  };
};

export const getSliderStyles = () => {
  return {
    OuterContainer: <React.CSSProperties> {
      paddingLeft: sliderLeftRightPadding,
      paddingRight: sliderLeftRightPadding,
      WebkitTapHighlightColor: 'transparent',
      TapHighlightColor: 'transparent',
    },
    TrackAndHandleContainer: <React.CSSProperties> {
      position: 'relative',
      height: sliderHeight,
    },
  };
};
